import React, { useEffect, useMemo, useRef, useState } from "react";

export interface ProjectItem {
  id: string;
  name: string;
  description?: string;
  isRunning: boolean;
}

interface Props {
  projects: ProjectItem[];
  defaultSelectedId?: string | null;
  onChange?: (project: ProjectItem | null) => void;
  allowAllProject?: boolean;
  placeholder?: string;
  buttonClassName?: string;
  containerClassName?: string;
  disabled?: boolean;
  showSublabel?: boolean;
}

const ProjectFilterDropdown: React.FC<Props> = ({
  projects,
  defaultSelectedId,
  onChange,
  allowAllProject = false,
  placeholder,
  buttonClassName,
  containerClassName = "",
  disabled = false,
  showSublabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(
    defaultSelectedId ? defaultSelectedId : null
  );
  const prevDefaultSelectedIdRef = useRef(defaultSelectedId);

  useEffect(() => {
    if (defaultSelectedId === prevDefaultSelectedIdRef.current) return;
    prevDefaultSelectedIdRef.current = defaultSelectedId;
    setSelectedId(defaultSelectedId ? defaultSelectedId : null);
  }, [defaultSelectedId]);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // tutup kalau klik di luar
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // reset search tiap kali dropdown ditutup
  useEffect(() => {
    if (!isOpen) setSearchQuery("");
  }, [isOpen]);

  useEffect(() => {
    if (allowAllProject) return;
    if (selectedId) return;
    if (projects.length === 0) return;

    const fallback = projects[0];
    setSelectedId(fallback.id);
    onChange?.(fallback);
  }, [allowAllProject, projects, selectedId]);

  const selectedProject = useMemo(
    () => projects.find((p) => String(p.id) === String(selectedId)) ?? null,
    [projects, selectedId]
  );

  // state search tanpa hit api baru
  const { running, closed, isCategorized } = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const matched = query
      ? projects.filter((p) => p.name.toLowerCase().includes(query))
      : projects;

    const hasCategorization = matched.some((p) => p.isRunning !== undefined);

    if (!hasCategorization) {
      return { running: matched, closed: [], isCategorized: false };
    }

    return {
      running: matched.filter((p) => p.isRunning),
      closed: matched.filter((p) => !p.isRunning),
      isCategorized: true,
    };
  }, [projects, searchQuery]);

  const handleSelect = (project: ProjectItem) => {
    setSelectedId(project.id);
    setIsOpen(false);
    onChange?.(project);
  };

  const displayPlaceholder = placeholder ?? (allowAllProject ? "All Projects" : "Select a Project");
  const isSublabelShown = showSublabel ?? (buttonClassName ? false : true);

  return (
    <div ref={wrapperRef} className={`dropdown position-relative d-inline-block w-100 ${containerClassName}`}>
      <button
        type="button"
        disabled={disabled}
        className={`${buttonClassName ?? "btn border d-flex justify-content-between align-items-center w-100 bg-white text-start"} ${disabled ? "disabled opacity-50 pe-none" : ""}`}
        style={buttonClassName ? undefined : { minHeight: "38px" }}
        onClick={() => setIsOpen((s) => !s)}
      >
        <span className="d-block text-truncate" style={{ minWidth: 0, flex: 1 }}>
          {isSublabelShown && (
            <div className="small text-muted text-uppercase" style={{ fontSize: 10 }}>
              Selected Project
            </div>
          )}
          <div
            className={`text-truncate ${isSublabelShown ? "fw-semibold" : ""}`}
            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            title={selectedProject ? selectedProject.name : displayPlaceholder}
          >
            {selectedProject ? selectedProject.name : displayPlaceholder}
          </div>
        </span>
        <i className={`fa fa-chevron-${isOpen ? "up" : "down"} flex-shrink-0 ms-2`} style={{ fontSize: "0.75rem" }} />
      </button>

      {isOpen && (
        <div
          className="card shadow show position-absolute mt-1 animate-pop-in"
          style={{ minWidth: 260, width: "100%", zIndex: 1050 }}
        >
          <div className="p-2 border-bottom">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search Project..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {allowAllProject && (
              <div className="px-2 pt-2 border-bottom pb-2">
                <button
                  type="button"
                  className={`dropdown-item dropdown-item-hover d-flex align-items-center justify-content-between py-1.5 px-2 rounded ${selectedId === null ? "active bg-primary text-white fw-bold" : "fw-medium text-dark"
                    }`}
                  onClick={() => {
                    setSelectedId(null);
                    setIsOpen(false);
                    onChange?.(null);
                  }}
                >
                  <span className="d-flex align-items-center gap-2" style={{ minWidth: 0, overflow: "hidden" }}>
                    <i className="fa fa-list-check flex-shrink-0" style={{ width: 14 }} />
                    <span className="text-truncate">All Projects</span>
                  </span>
                  {selectedId === null && (
                    <i className="fa fa-check-circle my-auto flex-shrink-0" />
                  )}
                </button>
              </div>
            )}

            {isCategorized && running.length > 0 && (
              <div className="px-2 pt-2">
                <div className="d-flex justify-content-between align-items-center px-1 mb-1">
                  <span
                    className="text-muted fw-semibold"
                    style={{ fontSize: 11, letterSpacing: 0.5 }}
                  >
                    RUNNING PROJECT
                  </span>
                  <span className="badge bg-success-subtle text-success" style={{ fontSize: 10 }}>
                    ACTIVE
                  </span>
                </div>
                <ul className="list-unstyled mb-0">
                  {running.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className={`dropdown-item dropdown-item-hover d-flex align-items-center justify-content-between py-1 px-2 rounded ${String(selectedId) === String(p.id) ? "active bg-primary text-white fw-bold" : ""
                          }`}
                        onClick={() => handleSelect(p)}
                      >
                        <span className="d-flex align-items-center gap-2" style={{ minWidth: 0, overflow: "hidden" }}>
                          <span
                            className="rounded-circle d-inline-block flex-shrink-0"
                            style={{ width: 8, height: 8, backgroundColor: String(selectedId) === String(p.id) ? "#ffffff" : "#22c55e" }}
                          />
                          <span className="text-truncate" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={p.name}>
                            {p.name}
                          </span>
                        </span>
                        {String(selectedId) === String(p.id) && (
                          <i className="fa fa-check-circle my-auto flex-shrink-0" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isCategorized && closed.length > 0 && (
              <div className="px-2 pt-2 pb-2">
                <div className="px-1 mb-1">
                  <span
                    className="text-muted fw-semibold"
                    style={{ fontSize: 11, letterSpacing: 0.5 }}
                  >
                    CLOSED PROJECT
                  </span>
                </div>
                <ul className="list-unstyled mb-0">
                  {closed.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className={`dropdown-item dropdown-item-hover d-flex align-items-center justify-content-between py-1 px-2 rounded ${String(selectedId) === String(p.id) ? "active bg-primary text-white fw-bold" : ""
                          }`}
                        onClick={() => handleSelect(p)}
                      >
                        <span className="d-flex align-items-center gap-2" style={{ minWidth: 0, overflow: "hidden" }}>
                          <span
                            className="rounded-circle d-inline-block flex-shrink-0"
                            style={{ width: 8, height: 8, backgroundColor: String(selectedId) === String(p.id) ? "#ffffff" : "#9ca3af" }}
                          />
                          <span className="text-truncate" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={p.name}>
                            {p.name}
                          </span>
                        </span>
                        {String(selectedId) === String(p.id) && (
                          <i className="fa fa-check-circle my-auto flex-shrink-0" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!isCategorized && running.length > 0 && (
              <div className="px-2 pt-2 pb-2">
                <div className="px-1 mb-1">
                  <span
                    className="text-muted fw-semibold"
                    style={{ fontSize: 11, letterSpacing: 0.5 }}
                  >
                    PROJECT LIST
                  </span>
                </div>
                <ul className="list-unstyled mb-0">
                  {running.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className={`dropdown-item dropdown-item-hover d-flex align-items-center justify-content-between py-1 px-2 rounded ${String(selectedId) === String(p.id) ? "active bg-primary text-white fw-bold" : ""
                          }`}
                        onClick={() => handleSelect(p)}
                      >
                        <span className="d-flex align-items-center gap-2" style={{ minWidth: 0, overflow: "hidden" }}>
                          <span
                            className="rounded-circle d-inline-block flex-shrink-0"
                            style={{ width: 8, height: 8, backgroundColor: String(selectedId) === String(p.id) ? "#ffffff" : "#2563eb" }}
                          />
                          <span className="text-truncate" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={p.name}>
                            {p.name}
                          </span>
                        </span>
                        {String(selectedId) === String(p.id) && (
                          <i className="fa fa-check-circle my-auto flex-shrink-0" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {running.length === 0 && closed.length === 0 && (
              <div className="text-center text-muted py-3 small">
                No project found.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectFilterDropdown;